import { faXmark } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Divider, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { IPage } from "page/model"
import { navigate } from "page/tools/navigate"
import React from "react"
import { connect } from "react-redux"
import { HEADER_PHONE_HEIGHT_IN_REM, IMenuGroup, IMenuItem } from "ui/model"
import { setMenuExpansion } from "ui/tools/menu"
import { UserActionButtons } from "user/components/UserActionButtons"
import { getUserAgencyOrEmail, getUserDisplayName } from "user/tools/common"
import { FlexBox } from "../FlexBox"
import { Logo } from "../Logo"

interface IMenuItemProps {
  menu: IMenuItem
  isActive: boolean
  isFirst: boolean
}

interface IMenuPhoneProps {
  isOpened: boolean
  menus: IMenuItem[]
  activePage: IPage
}

const MenuItem = React.memo(({ menu, isActive, isFirst }: IMenuItemProps) => {
  const isGroup: boolean = menu.hasOwnProperty("items")
  return (
    <Box
      sx={{
        "zIndex": 1,
        "width": "100%",
        "position": "relative",
        "height": "2.5rem",
        "mb": ".3rem",
        "display": "flex",
        "alignItems": "center",
        "justifyContent": "start",
        "cursor": !isActive && menu.pagePaths.length > 0 ? "pointer" : undefined,
        "pl": !isGroup ? "3rem" : "1.2rem",
        "pr": "1.2rem",
        "borderRadius": ".25rem",
        "backgroundColor": theme => (isActive ? theme.palette.primary.main : "transparent"),
        "color": theme => (isActive ? theme.palette.specialText : theme.palette.text.primary),
        "transition": "background-color 300ms ease",
        "&:hover": {
          backgroundColor: theme =>
            isActive
              ? theme.palette.primary.main
              : menu.pagePaths.length > 0
                ? theme.palette.background.paper
                : "transparent"
        }
      }}
      onClick={() => {
        if (!isActive && menu.pagePaths.length > 0) {
          navigate(menu.pagePaths[0])
          setMenuExpansion(false)
        }
      }}>
      {!isFirst && isGroup && <Divider sx={{ width: "100%", position: "absolute", top: "-.2rem", left: 0 }} />}
      {menu.icon && <FontAwesomeIcon icon={menu.icon} size="lg" />}
      <Typography sx={{ ml: ".5rem", fontSize: ".9rem", fontWeight: isGroup ? 500 : undefined }}>
        {isGroup ? translate(menu.titleKey).toUpperCase() : translate(menu.titleKey)}
      </Typography>
    </Box>
  )
})

export const MenuPhoneFn = React.memo(({ isOpened, menus, activePage }: IMenuPhoneProps) => {
  if (!isOpened) {
    return null
  }
  return (
    <FlexBox
      vertical
      fullHeight
      jc="flex-start"
      sx={{
        position: "fixed",
        zIndex: 10,
        width: "100%",
        minWidth: "100%",
        maxWidth: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.50)", // theme => theme.palette.background.paper,
        animation: "fadeIn 300ms"
      }}
      onClick={event => {
        event.stopPropagation()
        setMenuExpansion(false)
      }}>
      <FlexBox
        vertical
        fullHeight
        jc="space-between"
        sx={{
          position: "fixed",
          width: "calc(100% - 4.3rem)",
          maxWidth: "calc(100% - 4.3rem)",
          minWidth: "16.5rem",
          left: 0,
          backgroundColor: theme => theme.palette.background.default,
          animation: "openLeftRight 300ms",
          borderRight: theme => theme.palette.border
        }}
        onClick={event => event?.stopPropagation()}>
        <Box
          sx={{
            display: "flex",
            height: "calc(100% - 5.5rem)",
            overflow: "hidden",
            flexDirection: "column"
          }}>
          <Box
            className="header"
            sx={{
              backgroundColor: theme => theme.palette.background.paper,
              borderBottom: theme => theme.palette.border,
              height: HEADER_PHONE_HEIGHT_IN_REM,
              minHeight: HEADER_PHONE_HEIGHT_IN_REM,
              maxHeight: HEADER_PHONE_HEIGHT_IN_REM
            }}>
            <Logo
              sx={{
                width: "7rem",
                height: "3rem",
                ml: "1rem"
              }}
            />
            <Box
              sx={{
                "position": "absolute",
                "border": theme => `1px solid ${theme.palette.text.primary}`,
                "borderRadius": ".25rem",
                "top": ".5rem",
                "right": ".5rem",
                "borderTopRightRadius": ".2rem",
                "borderBottomRightRadius": ".2rem",
                "width": "2rem !important",
                "height": "2rem",
                "display": "flex",
                "alignItems": "center",
                "justifyContent": "center",
                "cursor": "pointer",
                "backgroundColor": theme => theme.palette.background.paper,
                "& .menu-close": {
                  color: theme => theme.palette.text.primary
                }
              }}
              onClick={() => setMenuExpansion(false)}>
              <FontAwesomeIcon icon={faXmark} size="lg" className="menu-close" />
            </Box>
          </Box>
          <Box
            sx={{
              width: "calc(100% - 2rem)",
              mt: "1rem",
              ml: "1rem",
              mr: "1rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap"
            }}>
            <Typography color="textPrimary" sx={{ mr: "1rem" }}>
              {getUserDisplayName()}
            </Typography>
            <Typography color="textPrimary" sx={{ fontWeight: 500 }}>
              {getUserAgencyOrEmail()}
            </Typography>
          </Box>
          <Divider sx={{ width: "calc(100% - 2rem)", mt: "1rem", ml: "1rem", mb: "1rem" }} />
          <Box
            sx={{
              pl: "1rem",
              pr: "1rem",
              height: "100%",
              overflow: "auto"
            }}>
            {menus.map((menu, index) => (
              <React.Fragment key={index}>
                <MenuItem
                  key={`group-${index}`}
                  menu={menu}
                  isActive={menu.pagePaths.includes(activePage.path)}
                  isFirst={index === 0}
                />
                {(menu as IMenuGroup).items
                  ? (menu as IMenuGroup).items.map((subMenu, subIndex) => (
                      <MenuItem
                        key={`${index}-${subIndex}`}
                        menu={subMenu}
                        isActive={subMenu.pagePaths.includes(activePage.path)}
                        isFirst={index === 0}
                      />
                    ))
                  : null}
              </React.Fragment>
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            width: "calc(100% - 2rem)",
            m: "1rem",
            display: "flex",
            flexDirection: "column"
          }}>
          <Divider sx={{ mb: "1rem" }} />
          <UserActionButtons displaySettings />
        </Box>
      </FlexBox>
    </FlexBox>
  )
})

export const MenuPhone = connect(
  (state: IRootState): IMenuPhoneProps => ({
    isOpened: state.ui.isMenuOpened,
    activePage: state.page.activePage,
    menus: state.ui.menus
  })
)(MenuPhoneFn)
